import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Package, MapPin, User, MessageSquare, CheckCircle, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useImpersonation } from "@/hooks/useImpersonation";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/hooks/useLanguage";
import { useSanitizedHTML } from "@/hooks/useSanitizedHTML";
import { formatPriceWithCurrency } from "@/utils/currency";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ConversationView } from "@/components/ConversationView";
import { OrderTracker } from "@/components/OrderTracker";
import { RegenerateQRButton } from "@/components/vendor/RegenerateQRButton";

import { VendorQRScanner } from "@/components/vendor/VendorQRScanner";
import { VendorDepositQRCode } from "@/components/vendor/VendorDepositQRCode";

interface OrderDetails {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
  available_at: string | null;
  picked_up_at: string | null;
  client_id: string;
  relay_id: string;
  product_id: string;
  products?: {
    title: string;
    description: string;
    images: string[] | null;
  };
  relays?: {
    name: string;
    address: string;
    contact_phone: string;
  };
  client_profile?: {
    full_name: string | null;
  };
}

const VendorOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEffectiveUserId } = useImpersonation();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { displayCurrency, convertPrice } = useCurrency();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [markingAvailable, setMarkingAvailable] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showDepositQR, setShowDepositQR] = useState(false);

  // Sanitize product description
  const sanitizedDescription = useSanitizedHTML(order?.products?.description || '');

  const fetchOrderDetails = useCallback(async () => {
    if (!orderId) return;
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          products (
            title,
            description,
            images
          ),
          relays (
            name,
            address,
            contact_phone
          ),
          client_profile:profiles!orders_client_id_fkey (
            full_name
          )
        `)
        .eq('id', orderId)
        .eq('vendor_id', effectiveUserId)
        .single();

      if (error) throw error;

      if (!data) {
        toast.error('Commande non trouvée');
        navigate('/vendor-account');
        return;
      }

      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Erreur lors du chargement de la commande');
    } finally {
      setLoading(false);
    }
  }, [orderId, getEffectiveUserId, navigate]);

  useEffect(() => {
    if (orderId && user) {
      fetchOrderDetails();
    }

    // Subscribe to real-time order updates
    const channel = supabase
      .channel('vendor-order-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`
        },
        () => {
          if (orderId && user) {
            fetchOrderDetails();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId, user, fetchOrderDetails]);

  const getStatusLabel = (status: string) => {
    const fallbackByLang: Record<string, Record<string, string>> = {
      fr: { CANCELLED: 'Annulé' },
      en: { CANCELLED: 'Cancelled' },
      'lg-fr': { CANCELLED: 'Ebatalaki' },
      sw: { CANCELLED: 'Imeghairiwa' },
    };
    const fallback = fallbackByLang[language]?.[status] ?? fallbackByLang['fr'][status] ?? status;
    return t(`orders.status.${status}`, fallback);
  };

  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      'PENDING_PAYMENT': 'outline',
      'PAID': 'default',
      'AWAITING_DROP_OFF': 'secondary',
      'AVAILABLE_FOR_PICKUP': 'default',
      'PICKED_UP': 'secondary',
      'CANCELLED': 'destructive',
      'EXPIRED': 'destructive'
    };
    return variants[status] || 'outline';
  };

  const handleMarkAvailable = async () => {
    if (!order || markingAvailable) return;

    try {
      setMarkingAvailable(true);
      
      // Send vendor QR code email
      const { error: vendorEmailError } = await supabase.functions.invoke('send-vendor-qr-code', {
        body: { orderId: order.id }
      });

      if (vendorEmailError) {
        console.error('Error sending vendor QR code:', vendorEmailError);
        toast.error('Erreur lors de l\'envoi du QR code vendeur');
      } else {
        toast.success('QR code vendeur envoyé par email');
        // Open scanner for immediate use
        setShowQRScanner(true);
      }
    } catch (error) {
      console.error('Error sending vendor QR code:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setMarkingAvailable(false);
    }
  };

  const handleQRSuccess = () => {
    setShowQRScanner(false);
    fetchOrderDetails();
    toast.success('Commande prête pour retrait. Le client a été notifié.');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-subtle py-12">
          <div className="container mx-auto px-4">
            <div className="animate-pulse">Chargement...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-subtle py-12">
          <div className="container mx-auto px-4">
            <p>Commande non trouvée</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-subtle py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/vendor-account')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Commande {order.order_number}</h1>
                <p className="text-muted-foreground">
                  Passée le {new Date(order.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
            <Badge variant={getStatusVariant(order.status)} className="text-base px-4 py-2">
              {getStatusLabel(order.status)}
            </Badge>
          </div>

          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Détails</TabsTrigger>
              <TabsTrigger value="messages">
                <MessageSquare className="h-4 w-4 mr-2" />
                Messages
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-6 mt-6">
              {/* Action Button */}
              {order.status === 'PAID' && (
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="font-semibold mb-1">Produit prêt à déposer ?</h3>
                        <p className="text-sm text-muted-foreground">
                          Montrez le QR code au point relais pour confirmer le dépôt
                        </p>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                          onClick={() => setShowDepositQR(true)}
                          size="lg"
                          className="flex-1 sm:flex-none"
                        >
                          <QrCode className="h-4 w-4 mr-2" />
                          Afficher QR Code
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* QR Code Regeneration - visible pour commandes disponibles */}
              {(order.status === 'AVAILABLE_FOR_PICKUP' || order.status === 'PICKED_UP') && (
                <Card className="border-blue-200 bg-blue-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold mb-1">QR Code de retrait</h3>
                        <p className="text-sm text-muted-foreground">
                          Régénérer et renvoyer le QR code au client par email
                        </p>
                      </div>
                      <RegenerateQRButton 
                        orderId={order.id} 
                        orderNumber={order.order_number}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Product Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Produit commandé
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    {order.products?.images?.[0] && (
                      <img
                        src={order.products.images[0]}
                        alt={order.products.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{order.products?.title}</h3>
                      <div 
                        className="text-muted-foreground mt-1" 
                        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                      />
                      <p className="font-bold text-xl mt-3">
                        {formatPriceWithCurrency(order.amount_cents, order.currency, displayCurrency, convertPrice)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Client Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold">
                    {order.client_profile?.full_name || 'Client'}
                  </p>
                </CardContent>
              </Card>

              {/* Relay Point Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Point de retrait
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="font-semibold break-words">{order.relays?.name}</p>
                    <p className="text-sm text-muted-foreground break-words">{order.relays?.address}</p>
                    <p className="text-sm break-words">Tél: {order.relays?.contact_phone}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Order Tracker */}
              <Card>
                <CardHeader>
                  <CardTitle>Suivi de commande</CardTitle>
                </CardHeader>
                <CardContent>
                  <OrderTracker
                    createdAt={order.created_at}
                    paidAt={order.paid_at}
                    availableAt={order.available_at}
                    pickedUpAt={order.picked_up_at}
                    status={order.status}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="messages" className="mt-6">
              <Card className="min-h-[600px]">
                <CardContent className="p-0 h-[600px]">
                  <ConversationView
                    otherUserId={order.client_id}
                    otherUserName={order.client_profile?.full_name || 'Client'}
                    initialSubject={`Commande ${order.order_number}`}
                    orderId={order.id}
                    onBack={() => {}}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <VendorQRScanner
        open={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onSuccess={handleQRSuccess}
        orderId={orderId!}
      />

      <VendorDepositQRCode
        open={showDepositQR}
        onClose={() => setShowDepositQR(false)}
        onSuccess={() => {
          setShowDepositQR(false);
          fetchOrderDetails();
        }}
        orderId={orderId!}
        orderNumber={order?.order_number || ''}
      />

      <Footer />
    </>
  );
};

export default VendorOrderDetail;
